import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { ChatMessage } from "@/types";
import {
    startOrderReturnRequest,
    chatToAiForReturn,
    submitReturnRequest,
    uploadReturnEvidence as uploadReturnEvidenceApi,
    evaluateReturnIssue,
} from "@/service/returnChatService";
import { RootState } from "../store";

interface ReturnChatState {
    currentChat: ChatMessage[];
    currentChatToGpt: ChatMessage[];
    isLoading: boolean;
    isUploading: boolean;
    showSubmitReturn: boolean;
    showConfirmSubmitModal: boolean;
    returnId: string;        // return entry ID from StartOrderReturnRequest
    orderId: string;
    productId: string;
    merchantId: string;
    replacementProductId: string; // ID of the product selected for replacement
    submitted: boolean;
    flowStep: number;
    evaluationResult: any | null;
    isEvaluating: boolean;
    selectedSolution: string | null;
    selectedRefundMethod: string | null;
    customerAddress: any | null;
    suggestedProducts: any[];
    isCancelled: boolean;
    supportRequested: boolean;
    showCancelConfirmation: boolean;
    status: string | null;
}

const initialState: ReturnChatState = {
    currentChat: [],
    currentChatToGpt: [],
    isLoading: false,
    isUploading: false,
    showSubmitReturn: false,
    showConfirmSubmitModal: false,
    returnId: "",
    orderId: "",
    productId: "",
    merchantId: "",
    replacementProductId: "",
    submitted: false,
    flowStep: 0,
    evaluationResult: null,
    isEvaluating: false,
    selectedSolution: null,
    selectedRefundMethod: null,
    customerAddress: null,
    suggestedProducts: [],
    isCancelled: false,
    supportRequested: false,
    showCancelConfirmation: false,
    status: null,
};

// ─── HELPER: Check if return has enough info to submit ────────────────────────
const checkReturnReadiness = (state: ReturnChatState): boolean => {
    const { currentChat } = state;
    const userMessages = currentChat.filter((msg) => msg.role === "user");

    // Count images uploaded by user
    let imageCount = 0;
    userMessages.forEach((msg) => {
        const content = Array.isArray(msg.content) ? msg.content : [];
        content.forEach((c: any) => {
            if (c.type === "image_url") imageCount++;
        });
    });

    // Count text messages from user
    let textMessageCount = 0;
    userMessages.forEach((msg) => {
        const content = Array.isArray(msg.content) ? msg.content : [{ type: "text", text: String(msg.content) }];
        const hasText = content.some(
            (c: any) =>
                c.type === "text" &&
                typeof c.text === "string" &&
                c.text.trim().length > 0,
        );
        if (hasText) textMessageCount++;
    });

    // Need at least 1 image AND 2 text messages for returns
    if (imageCount < 1 || textMessageCount < 2) return false;

    // Check for wrong image issues from last assistant message
    const lastAssistantMessage = [...currentChat]
        .reverse()
        .find((msg) => msg.role === "assistant");

    if (lastAssistantMessage) {
        const content = Array.isArray(lastAssistantMessage.content) 
            ? lastAssistantMessage.content 
            : [{ type: "text", text: String(lastAssistantMessage.content) }];

        const lastTextContent = content.find(
            (c: any) => c.type === "text",
        );
        const lastText = (lastTextContent?.text || "").toLowerCase();

        const wrongImageKeywords = [
            "mix-up", "mix up", "mixup", "wrong image", "wrong photo",
            "wrong picture", "incorrect image", "incorrect photo",
            "not of the", "not the correct", "not the right",
            "different product", "wrong product", "doesn't match",
            "does not match", "not related", "unrelated image", "unrelated photo",
        ];

        if (wrongImageKeywords.some((phrase) => lastText.includes(phrase))) {
            return false;
        }
    }

    return true;
};

// ─── Async Thunks ─────────────────────────────────────────────────────────────

/**
 * Start a return request via POST /customer/StartOrderReturnRequest
 */
export const createReturnRequest = createAsyncThunk(
    "returnChat/createReturnRequest",
    async (payload: { order_id: string; product_id: string }) => {
        return await startOrderReturnRequest(payload);
    },
    {
        condition: (_, { getState }) => {

            const { returnChat } = getState() as RootState;
            // Block ONLY if already loading to prevent double-firing
            if (returnChat.isLoading) return false;
            return true;
        }
    }
);

/**
 * Send a chat message via POST /customer/chatToAiForReturn
 */
export const sendReturnMessage = createAsyncThunk(
    "returnChat/sendMessage",
    async (args: { message: ChatMessage }, { getState, dispatch }) => {

        const state = getState() as RootState;
        const { returnId, currentChatToGpt } = state.returnChat;

        // Build messagesArr: existing conversation + new message
        const messagesArr = [...currentChatToGpt, args.message];

        const response = await chatToAiForReturn({
            return_id: returnId,
            messagesArr,
        });

        if (response.is_ready_for_resolution) {
            // Trigger evaluation automatically when AI signals readiness
            dispatch(evaluateReturn(returnId));
        }

        return response;

    },
);

/**
 * Upload evidence for a return.
 */
export const uploadReturnEvidence = createAsyncThunk(
    "returnChat/uploadEvidence",
    async (args: { formData: FormData; helperText?: string }) => {
        const response = await uploadReturnEvidenceApi(args.formData);
        return { ...response, helperText: args.helperText };
    },
);

/**
 * Submit the return request via POST /customer/submitCancelRequest
 */
export const submitReturn = createAsyncThunk(
    "returnChat/submitReturn",
    async (payload: { status?: string } | undefined, { getState }) => {
        const state = getState() as RootState;
        const { returnId, selectedSolution, customerAddress, replacementProductId, evaluationResult } = state.returnChat;
        
        let finalSolution = selectedSolution;
        let submitStatus = payload?.status || null;
        let closureReason = null;
        let internalClosureNotes = null;

        if (!submitStatus && selectedSolution?.toLowerCase().includes('refund')) {
            finalSolution = "refund";
            submitStatus = 'refund_request_initiated';
        }

        if (submitStatus === 'rejected') {
            closureReason = "Claim rejected due to evidence discrepancies";
            internalClosureNotes = evaluationResult?.summary || "Auto-rejected: legitimacy score < 0.45";
        }

        return await submitReturnRequest(returnId, {
            selected_solution: finalSolution,
            status: submitStatus,
            customer_address: Object.keys(customerAddress || {}).filter(k => k !== 'email').length > 0 ? customerAddress : null,
            replacement_product_id: replacementProductId,
            closure_reason: closureReason,
            internal_closure_notes: internalClosureNotes
        });
    },
);

/**
 * AI evaluation of the return request
 */
export const evaluateReturn = createAsyncThunk(
    "returnChat/evaluateReturn",
    async (returnId: string) => {
        const response = await evaluateReturnIssue(returnId);
        return response;
    },
    {
        condition: (_, { getState }) => {
            const { returnChat } = getState() as RootState;
            // Prevent double-firing if already evaluating or if we have results
            if (returnChat.evaluationResult) return false;
            return true;
        }
    }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const returnChatSlice = createSlice({
    name: "returnChat",
    initialState,
    reducers: {
        setReturnChat: (state, action: PayloadAction<ChatMessage[]>) => {
            state.currentChat = action.payload;
        },
        setReturnChatToGpt: (state, action: PayloadAction<ChatMessage[]>) => {
            state.currentChatToGpt = action.payload;
        },
        addLocalReturnMessage: (state, action: PayloadAction<ChatMessage>) => {
            const msg: ChatMessage = {
                ...action.payload,
                content: Array.isArray(action.payload.content) 
                    ? (action.payload.content as any)
                    : [{ type: "text", text: String(action.payload.content) }]
            };
            state.currentChat.push(msg as any);
            state.currentChatToGpt.push(msg as any);
        },
        setReturnId: (state, action: PayloadAction<string>) => {
            state.returnId = action.payload;
        },
        openReturnConfirmModal: (state) => {
            state.showConfirmSubmitModal = true;
        },
        closeReturnConfirmModal: (state) => {
            state.showConfirmSubmitModal = false;
        },
        resetReturnState: () => {
            const refreshedState = { ...initialState };
            refreshedState.currentChat = [];
            refreshedState.currentChatToGpt = [];
            return refreshedState;
        },
        resetCancelConfirmation: (state) => {
            state.showCancelConfirmation = false;
        },
        clearReturnChat: (state) => {
            state.currentChat = [];
            state.currentChatToGpt = [];
            state.returnId = "";
            state.orderId = "";
            state.productId = "";
            state.merchantId = "";
            state.submitted = false;
            state.showSubmitReturn = false;
            state.showConfirmSubmitModal = false;
            state.flowStep = 0;
            state.evaluationResult = null;
            state.isEvaluating = false;
            state.selectedSolution = null;
            state.selectedRefundMethod = null;
            state.customerAddress = null;
            state.suggestedProducts = [];
        },
        setSelectedSolution: (state, action: PayloadAction<string>) => {
            state.selectedSolution = action.payload;
        },
        setSelectedRefundMethod: (state, action: PayloadAction<string>) => {
            state.selectedRefundMethod = action.payload;
        },
        setFlowStep: (state, action: PayloadAction<number>) => {
            state.flowStep = action.payload;
        },
        setSupportRequested: (state, action: PayloadAction<boolean>) => {
            state.supportRequested = action.payload;
            if (action.payload) {
                state.status = "support_requested";
            }
        },
        setReplacementProductId: (state, action: PayloadAction<string>) => {
            state.replacementProductId = action.payload;
        },
        updateCustomerAddress: (state, action: PayloadAction<any>) => {
            state.customerAddress = action.payload;
        },
        processSequentialStep: (state) => {
            const lastUserMessage = [...state.currentChat].reverse().find(m => m.role === "user");
            let mainFileTypeName = "file";
            
            // Re-detect main file type from last message if needed
            if (lastUserMessage) {
                const hasImage = lastUserMessage.content.some(c => c.type === "image_url");
                const hasVideo = lastUserMessage.content.some(c => c.type === "video_url");
                const hasPdf = (lastUserMessage.content as any).some((c: any) => c.type === "pdf_url");
                
                if (hasImage && !hasVideo && !hasPdf) mainFileTypeName = "image";
                else if (!hasImage && hasVideo && !hasPdf) mainFileTypeName = "video";
                else if (!hasImage && !hasVideo && hasPdf) mainFileTypeName = "document";
            }

            let aiMessageText = "";
            if (state.flowStep === 2) {
                aiMessageText = "Thanks! Please also upload your purchase invoice or proof of purchase.";
                state.flowStep = 3;
            } else if (state.flowStep === 3) {
                // Evaluation trigger - no hardcoded message here
                state.flowStep = 4;
                state.isLoading = false;
                state.showSubmitReturn = checkReturnReadiness(state);
                return; // Exit early to avoid pushing empty message
            } else {
                aiMessageText = `Thanks for sharing the ${mainFileTypeName}. Could you tell me more about why you'd like to return this product? Once we have all the details, you'll be able to submit your return request.`;
            }

            const responseMessage: ChatMessage = {
                role: "assistant",
                content: [{ type: "text", text: aiMessageText }],
                timestamp: new Date().toISOString(),
            };

            state.currentChat.push(responseMessage);
            state.currentChatToGpt.push(responseMessage);
            state.isLoading = false;
            state.showSubmitReturn = checkReturnReadiness(state);
        },
        setIsLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setTriageComplete: (state) => {
            state.flowStep = 4;
            state.isEvaluating = true;
            state.isLoading = true;
        },
        pushBotMessage: (state, action: PayloadAction<string | string[] | { text: string | string[], msg_id?: string, is_ready_for_resolution?: boolean }>) => {
            const payload = action.payload;
            let texts: string[];
            let msgId: string | undefined;
            let isReady = false;

            if (typeof payload === 'object' && !Array.isArray(payload) && 'text' in payload) {
                texts = Array.isArray(payload.text) ? payload.text : [payload.text];
                msgId = payload.msg_id;
                isReady = payload.is_ready_for_resolution || false;
            } else {
                texts = Array.isArray(payload) ? (payload as string[]) : [payload as string];
            }

            if (isReady) {
                state.flowStep = 4;
                state.isEvaluating = true;
                state.isLoading = true;
                state.showSubmitReturn = false; // Close UI while evaluating
            }

            texts.forEach(t => {
                const responseMessage: ChatMessage = {
                    msg_id: msgId,
                    role: "assistant",
                    content: [{ type: "text", text: t }],
                    timestamp: new Date().toISOString(),
                };
                state.currentChat.push(responseMessage);
                state.currentChatToGpt.push(responseMessage);
            });
        },

    },
    extraReducers: (builder) => {
        builder
            // ─── createReturnRequest ────────────────────────────────────
            .addCase(createReturnRequest.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(createReturnRequest.fulfilled, (state, action) => {
                state.isLoading = false;
                state.returnId = action.payload.return_id;
                state.status = action.payload.status || null;

                const existingChat = action.payload.existing_chat;
                if (existingChat && Array.isArray(existingChat) && existingChat.length > 0) {
                    // Restore existing session
                    state.currentChat = existingChat;
                    state.currentChatToGpt = existingChat;
                    state.submitted = action.payload.submitted || false;
                    state.orderId = action.meta.arg.order_id;
                    state.productId = action.meta.arg.product_id;

                    // Restore evaluation data if it exists to prevent re-triggering on mount
                    if (action.payload.ai_evaluation) {
                        state.evaluationResult = {
                            ...action.payload.ai_evaluation,
                            status: action.payload.status,
                            category: action.payload.category,
                            reason: action.payload.reason,
                            legitimacy_score: action.payload.ai_confidence
                        };
                    }

                    // Restore flowStep based on status or message count
                    const currentStatus = action.payload.status;
                    if (currentStatus === "rejected" || currentStatus === "manual_review") {
                        state.flowStep = 6;
                    } else if (currentStatus === "cancelled") {
                        state.flowStep = 6;
                        state.isCancelled = true;
                    } else if (currentStatus === "support_requested") {
                        state.flowStep = 6;
                        state.supportRequested = true;
                    } else if (state.submitted) {
                        state.flowStep = 6;
                    } else {
                        // Heuristic for in-progress sessions
                        const assistantMsgs = existingChat.filter((m: any) => m.role === "assistant");
                        const userMsgs = existingChat.filter((m: any) => m.role === "user");
                        
                        if (assistantMsgs.some((m: any) => (m.content[0]?.text || "").includes("purchase invoice"))) {
                            state.flowStep = 3;
                        } else if (userMsgs.length >= 2) {
                            state.flowStep = 2;
                        } else if (userMsgs.length >= 1) {
                            state.flowStep = 1;
                        } else {
                            state.flowStep = 0;
                        }
                    }
                } else {
                    // Create new session
                    state.currentChat = [];
                    state.currentChatToGpt = [];
                    state.flowStep = 0;
                    state.submitted = false;
                    state.orderId = action.meta.arg.order_id;
                    state.productId = action.meta.arg.product_id;
                }
                state.showSubmitReturn = false;
            })
            .addCase(createReturnRequest.rejected, (state) => {
                state.isLoading = false;
            })

            // ─── sendReturnMessage ──────────────────────────────────────
            .addCase(sendReturnMessage.pending, (state) => {
                state.isLoading = true;
                state.showCancelConfirmation = false; // Reset on new message
            })
            .addCase(sendReturnMessage.fulfilled, (state, action) => {
                state.isLoading = false;
                state.suggestedProducts = action.payload.suggested_products || [];

                // Detect cancellation confirmation prompt
                const aiTextLower = (action.payload.message || "").toLowerCase();
                if (aiTextLower.includes("sure you want to cancel")) {
                    state.showCancelConfirmation = true;
                }
                
                // Handle actual AI response if it exists
                if (action.payload.success && action.payload.message) {
                    const aiText = action.payload.message;
                    
                    const aiMsg: ChatMessage = {
                        msg_id: action.payload.message_id,
                        role: "assistant",
                        content: [{ type: "text", text: aiText }],
                        timestamp: new Date().toISOString(),
                    };
                    state.currentChat.push(aiMsg);
                    state.currentChatToGpt.push(aiMsg);
                }

                if (action.payload.case_closed) {
                    state.flowStep = 6;
                    state.submitted = true;
                    state.isCancelled = true;
                }

                if (action.payload.is_ready_for_resolution) {
                    state.flowStep = 4;
                    state.isEvaluating = true;
                }

                // 2. Handle Flow Transitions

                if (action.payload.evaluation_triggered) {
                    state.flowStep = 4;
                } else if (state.suggestedProducts.length > 0) {
                    state.flowStep = 7;
                } else if (state.flowStep === 0) {
                    state.flowStep = 1;
                } else if (state.flowStep === 1) {
                    state.flowStep = 2;
                }

                // 2. Handle Flow Transitions (REMOVED: Steps 5, 7, 8, 9, 11, 12 are now handled by Step 10 after evaluation)
                
                state.showSubmitReturn = checkReturnReadiness(state);
            })
            .addCase(sendReturnMessage.rejected, (state) => {
                state.isLoading = false;
            })

            // ─── uploadReturnEvidence ───────────────────────────────────
            .addCase(uploadReturnEvidence.pending, (state) => {
                state.isUploading = true;
                state.isLoading = true;
            })
            .addCase(uploadReturnEvidence.fulfilled, (state, action) => {
                const metadata = action.payload?.uploadedMetadata ?? [];
                const helperText = action.payload?.helperText;

                const messageContents: any[] = [];

                // We will add helperText to the first media item instead of a separate block

                metadata.forEach((file: any) => {
                    const mime = file.mime || "";
                    const isImage = mime.startsWith("image/");
                    const isVideo = mime.startsWith("video/");
                    const isPdf = mime === "application/pdf";

                    if (isPdf) {
                        messageContents.push({ type: "pdf_url", pdf_url: { url: file.url, file_name: file.name } });
                    } else if (isImage) {
                        messageContents.push({ type: "image_url", image_url: { url: file.url } });
                    } else if (isVideo) {
                        messageContents.push({ type: "video_url", video_url: { url: file.url } });
                    } else {
                        messageContents.push({ type: "text", text: `📎 File uploaded: ${file.name}` });
                    }
                });

                // Add helper text as a separate text block if it exists
                if (helperText) {
                    messageContents.push({ type: "text", text: helperText });
                }

                const combinedUserMessage: ChatMessage = {
                    role: "user",
                    content: messageContents,
                    timestamp: new Date().toISOString(),
                };

                state.currentChat.push(combinedUserMessage);
                state.currentChatToGpt.push(combinedUserMessage);

                // Handle AI response from upload
                // Now backend returns success: true, but we should be resilient
                const isSuccessful = action.payload.success || (action.payload.uploaded > 0);
                
                if (isSuccessful && action.payload.message) {
                    const aiText = action.payload.message;
                    // Only push if it's NOT the generic "Files uploaded" technical message
                    if (aiText !== "Files uploaded") {
                        const aiMsg: ChatMessage = {
                            msg_id: action.payload.message_id,
                            role: "assistant",
                            content: [{ type: "text", text: aiText }],
                            timestamp: new Date().toISOString(),
                        };
                        state.currentChat.push(aiMsg);
                        state.currentChatToGpt.push(aiMsg);
                    }
                }

                if (action.payload.is_ready_for_resolution) {
                    state.flowStep = 4;
                    state.isEvaluating = true;
                }

                state.isUploading = false;
                state.isLoading = false;
                state.showSubmitReturn = checkReturnReadiness(state);
            })
            .addCase(uploadReturnEvidence.rejected, (state) => {
                state.isUploading = false;
                state.isLoading = false;
            })

            // ─── submitReturn ──────────────────────────────────────────
            .addCase(submitReturn.fulfilled, (state, action) => {
                state.submitted = action.payload.data.submitted;
                state.status = action.payload.data.status || state.status;
                state.isLoading = false;
                state.showSubmitReturn = false;
                state.showConfirmSubmitModal = false;
            })
            .addCase(submitReturn.rejected, (state) => {
                state.isLoading = false;
                state.showConfirmSubmitModal = false;
            })

            // ─── evaluateReturn ─────────────────────────────────────────
            .addCase(evaluateReturn.pending, (state) => {
                state.isEvaluating = true;
                state.isLoading = true;
            })
            .addCase(evaluateReturn.fulfilled, (state, action) => {
                state.isEvaluating = false;
                state.isLoading = false;
                
                if (!action.payload) return;

                state.evaluationResult = action.payload;
                state.suggestedProducts = action.payload.suggested_products || [];
                
                // Update local status if the backend confirms it (e.g. manual_review)
                if (action.payload.status) {
                    state.status = action.payload.status;
                }
                
                const score = action.payload?.legitimacy_score;
                
                if (score < 0.45) {
                    // Rejected State: Push rejection message if provided
                    const rejectedText = action.payload?.message || action.payload?.summary || "We've reviewed your request. Unfortunately, based on the information provided, we are unable to approve this return automatically.";
                    
                    const rejectedMsg: ChatMessage = {
                        role: "assistant",
                        content: [{ type: "text", text: rejectedText }],
                        timestamp: new Date().toISOString(),
                    };
                    state.currentChat.push(rejectedMsg);
                    state.currentChatToGpt.push(rejectedMsg);
                    
                    state.selectedSolution = "Rejected";
                    state.flowStep = 6;
                    state.submitted = true;
                    state.status = "rejected";
                } else if (score < 0.95) {
                    // Manual Review State: Push manual review message
                    const reviewText = action.payload?.message || action.payload?.summary || "I've reviewed your request. I'm transferring this to our specialized team for a quick manual review. We'll get back to you soon!";
                    
                    const reviewMsg: ChatMessage = {
                        role: "assistant",
                        content: [{ type: "text", text: reviewText }],
                        timestamp: new Date().toISOString(),
                    };
                    state.currentChat.push(reviewMsg);
                    state.currentChatToGpt.push(reviewMsg);
                    
                    state.selectedSolution = "Manual Review";
                    state.flowStep = 10;
                    state.submitted = true;
                    state.status = "manual_review";
                } else {
                    // Approved State: Push terminal verification message
                    const approvedText = action.payload?.message || action.payload?.summary || "Your request has been automatically approved! Once you select a resolution, we will proceed with the pickup shortly.";
                    
                    const approvedMsg: ChatMessage = {
                        role: "assistant",
                        content: [{ type: "text", text: approvedText }],
                        timestamp: new Date().toISOString(),
                    };
                    state.currentChat.push(approvedMsg);
                    state.currentChatToGpt.push(approvedMsg);
                    
                    state.status = "approved";
                    state.flowStep = 10;
                    state.submitted = true;
                }
            })
            .addCase(evaluateReturn.rejected, (state) => {
                state.isEvaluating = false;
                state.isLoading = false;
            });
    },
});

export const {
    setReturnChat,
    setReturnChatToGpt,
    addLocalReturnMessage,
    setReturnId,
    openReturnConfirmModal,
    closeReturnConfirmModal,
    clearReturnChat,
    setFlowStep,
    setSupportRequested,
    processSequentialStep,
    pushBotMessage,
    setTriageComplete,
    setIsLoading,
    setSelectedSolution,
    setSelectedRefundMethod,
    updateCustomerAddress,
    setReplacementProductId,
    resetReturnState,
    resetCancelConfirmation,
} = returnChatSlice.actions;

export default returnChatSlice.reducer;
